// World renderer: baked tile map, y-sorted sprites, effects, then Diablo-style dithered lighting.

import { CLASSES } from '../../shared/classes';
import { MONSTERS } from '../../shared/monsters';
import { ANIM_CAST, ANIM_DEAD, ANIM_SHOOT, ANIM_SLAM, ANIM_SWING, ANIM_WALK, ANIM_WHIRL, ELEMENT_COLORS } from '../../shared/protocol';
import { RARITY_COLORS } from '../../shared/items';
import { dressedLook } from '../../shared/look';
import { parseZone } from '../../shared/realms';
import { tileNoise } from '../../shared/rng';
import { BUFF_BITS, ClassId, HeroLook, STATUS_BITS, T_BLOCK, T_FLOOR, T_LIQUID, T_VOID, T_WALL } from '../../shared/types';
import { drawText, textWidth } from '../gfx/font';
import { blended, blitHeld, blitHero, heldPose, heldReach, heldWeapon, heroFrame, itemIcon, monsterSprite, tinted } from '../gfx/sprites';
import { objectSprite, Tileset, tilesetFor } from '../gfx/tiles';
import { CEffect, CGround, CItem, CObject, CProj, CUnit, GameState, projColor } from './state';

const S = 16;

export const NPC_LOOKS: Record<string, HeroLook> = {
  merchant: { skin: '#f8b878', hair: '#fcfcfc', armor: '#a81000', trim: '#f8b800', body: 'robe', hat: 'hood', weapon: 'staff' },
  trainer: { skin: '#c84c0c', hair: '#000000', armor: '#006800', trim: '#f8d878', body: 'plate', hat: 'none', weapon: 'sword' },
};
export const NPC_NAMES: Record<string, string> = { merchant: 'Quill the Merchant', trainer: 'Oda the Trainer' };

function rand(seed: number): number {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

/** Pixel-perfect ring. Canvas arcs anti-alias, which would ruin the look. */
function ring(g: CanvasRenderingContext2D, cx: number, cy: number, r: number, col: string, stepSkip = 0): void {
  if (r <= 0) return;
  g.fillStyle = col;
  const n = Math.max(12, Math.ceil(r * 6.3));
  for (let i = 0; i < n; i++) {
    if (stepSkip && i % stepSkip !== 0) continue;
    const a = (i / n) * Math.PI * 2;
    g.fillRect(Math.round(cx + Math.cos(a) * r), Math.round(cy + Math.sin(a) * r), 1, 1);
  }
}

/** Filled disc, optionally checkerboarded so that what's beneath still shows through. */
function disc(g: CanvasRenderingContext2D, cx: number, cy: number, r: number, col: string, dither = 0): void {
  g.fillStyle = col;
  const R = Math.ceil(r);
  const rr = r * r;
  const ox = Math.round(cx);
  const oy = Math.round(cy);
  for (let dy = -R; dy <= R; dy++) {
    const span = Math.floor(Math.sqrt(Math.max(0, rr - dy * dy)));
    if (rr - dy * dy < 0) continue;
    if (!dither) {
      g.fillRect(ox - span, oy + dy, span * 2 + 1, 1);
    } else {
      for (let dx = -span; dx <= span; dx++) {
        const px = ox + dx;
        const py = oy + dy;
        if (dither === 1 ? (px + py) % 2 === 0 : dither === 2 ? px % 2 === 0 && py % 2 === 0 : px % 4 === 0 && py % 4 === 0) g.fillRect(px, py, 1, 1);
      }
    }
  }
}

const BAYER4 = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5].map((v) => (v + 0.5) / 16);

interface Light {
  x: number;
  y: number;
  r: number;
  a: number;
}

const OUTLINE = { unit: '#fcfcfc' };

export class WorldRenderer {
  private mapCanvas: HTMLCanvasElement | null = null;
  private gen = -1;
  private tileset: Tileset | null = null;
  private liquidTiles: { x: number; y: number; t: number }[] = [];
  private blockTiles: { x: number; y: number; v: number }[] = [];
  private lightCanvas = document.createElement('canvas');
  private lightCtx = this.lightCanvas.getContext('2d')!;
  private lightImg: ImageData | null = null;
  private visSmooth = new Float32Array(0);
  camX = 0;
  camY = 0;
  time = 0;
  lastDraw = 0;
  /** What's under the cursor this frame. */
  hoverUnit: CUnit | null = null;
  hoverItem: CItem | null = null;
  hoverObject: CObject | null = null;

  private rebuild(st: GameState): void {
    const map = st.map!;
    const realm = st.realm!;
    const ts = tilesetFor(realm);
    this.tileset = ts;
    const c = document.createElement('canvas');
    c.width = map.w * S;
    c.height = map.h * S;
    const g = c.getContext('2d')!;
    g.imageSmoothingEnabled = false;
    this.liquidTiles = [];
    this.blockTiles = [];
    const salt = st.roomSeed ^ 0x5bd1;
    for (let y = 0; y < map.h; y++) {
      for (let x = 0; x < map.w; x++) {
        const t = map.tiles[y * map.w + x];
        const n = tileNoise(x, y, salt);
        const px = x * S;
        const py = y * S;
        const below = y + 1 < map.h ? map.tiles[(y + 1) * map.w + x] : T_WALL;
        if (t === T_WALL) {
          const faceVisible = below !== T_WALL;
          if (faceVisible) g.drawImage(ts.wallFace[Math.floor(n * ts.wallFace.length) % ts.wallFace.length], px, py);
          else g.drawImage(ts.wallTop[Math.floor(n * ts.wallTop.length) % ts.wallTop.length], px, py);
          // A rim of light where a wall meets open floor, so that room shapes read in the dark.
          const open = (ox: number, oy: number) => ox >= 0 && oy >= 0 && ox < map.w && oy < map.h && map.tiles[oy * map.w + ox] !== T_WALL;
          g.fillStyle = realm.wallPal[2];
          if (open(x - 1, y)) g.fillRect(px, py, 1, S);
          if (open(x + 1, y)) g.fillRect(px + S - 1, py, 1, S);
          if (open(x, y - 1)) g.fillRect(px, py, S, 1);
          if (!faceVisible) {
            g.fillStyle = realm.wallPal[1];
            if (open(x - 1, y)) g.fillRect(px + 1, py, 1, S);
            if (open(x + 1, y)) g.fillRect(px + S - 2, py, 1, S);
          }
        } else {
          // Floor beneath everything else so that sprites with holes show the ground.
          g.drawImage(ts.floors[Math.floor(n * 4) % 4], px, py);
          if (t === T_FLOOR) {
            const n2 = tileNoise(x, y, salt + 7);
            if (n2 < ts.decoChance) g.drawImage(ts.decos[Math.floor(n2 * 977) % ts.decos.length], px, py);
          } else if (t === T_LIQUID || t === T_VOID) {
            this.liquidTiles.push({ x, y, t });
          } else if (t === T_BLOCK) {
            this.blockTiles.push({ x, y, v: Math.floor(n * 997) % Math.max(1, ts.blocks.length) });
          }
        }
        // Shadow cast down from a wall onto the floor below it.
        if (t !== T_WALL && y > 0 && map.tiles[(y - 1) * map.w + x] === T_WALL) {
          g.fillStyle = 'rgba(0,0,0,0.3)';
          g.fillRect(px, py, S, 3);
        }
      }
    }
    this.mapCanvas = c;
    this.visSmooth = new Float32Array(map.w * map.h);
    this.gen = st.zoneGen;
  }

  private toScreenX(x: number): number {
    return Math.round(x * S - this.camX);
  }

  private toScreenY(y: number): number {
    return Math.round(y * S - this.camY);
  }

  screenToWorld(sx: number, sy: number): [number, number] {
    return [(sx + this.camX) / S, (sy + this.camY) / S];
  }

  draw(g: CanvasRenderingContext2D, st: GameState, W: number, H: number, mx: number, my: number, showNames: boolean, dt: number): void {
    if (!st.map || !st.realm) return;
    if (this.gen !== st.zoneGen) this.rebuild(st);
    this.time += dt;
    const me = st.units.get(st.myUid);
    const fx = me ? me.x : st.px;
    const fy = me ? me.y : st.py;
    const shakeX = st.shake > 0 ? Math.round((Math.random() - 0.5) * st.shake * 2) : 0;
    const shakeY = st.shake > 0 ? Math.round((Math.random() - 0.5) * st.shake * 2) : 0;
    this.camX = Math.round(fx * S - W / 2) + shakeX;
    this.camY = Math.round(fy * S - H / 2 + 10) + shakeY;

    g.fillStyle = '#000000';
    g.fillRect(0, 0, W, H);
    g.drawImage(this.mapCanvas!, -this.camX, -this.camY);

    this.drawLiquids(g, st, W, H);
    this.drawGrounds(g, st);
    this.drawTelegraphs(g, st);
    this.drawFlatObjects(g, st);
    this.drawItems(g, st);

    this.pickHover(st, mx, my);
    this.drawSorted(g, st, W, H);

    this.drawProjectiles(g, st);
    this.drawEffects(g, st);
    this.drawLines(g, st);
    this.drawParticles(g, st);

    this.drawLighting(g, st, W, H, dt);

    this.drawUnitBars(g, st);
    this.drawItemLabels(g, st, showNames, mx, my);
    this.drawFloats(g, st);
  }

  private drawLiquids(g: CanvasRenderingContext2D, st: GameState, W: number, H: number): void {
    const ts = this.tileset!;
    const frame = Math.floor(this.time * 3);
    for (const l of this.liquidTiles) {
      const px = l.x * S - this.camX;
      const py = l.y * S - this.camY;
      if (px < -S || py < -S || px > W || py > H) continue;
      const frames = l.t === T_LIQUID ? ts.liquid : ts.void;
      g.drawImage(frames[(frame + l.x + l.y * 3) & 3], px, py);
    }
  }

  private drawGrounds(g: CanvasRenderingContext2D, st: GameState): void {
    for (const gr of st.grounds.values()) this.drawGround(g, gr);
  }

  private drawGround(g: CanvasRenderingContext2D, gr: CGround): void {
    const cx = this.toScreenX(gr.x);
    const cy = this.toScreenY(gr.y);
    const r = gr.r * S;
    const t = this.time;
    const flick = Math.floor(t * 10);
    switch (gr.vis) {
      case 'fireGround': {
        disc(g, cx, cy, r, '#a81000', 2);
        disc(g, cx, cy, r * 0.75, '#f83800', 3);
        for (let i = 0; i < Math.min(26, r * 1.2); i++) {
          const a = rand(i + flick * 0.1) * 6.28;
          const d = rand(i * 3 + 1 + (flick >> 1)) * r;
          g.fillStyle = i % 3 === 0 ? '#f8d878' : '#f87800';
          const fh = 1 + (i % 3);
          g.fillRect(Math.round(cx + Math.cos(a) * d), Math.round(cy + Math.sin(a) * d) - fh, 1, fh);
        }
        break;
      }
      case 'poisonGround': {
        disc(g, cx, cy, r, '#006800', 1);
        for (let i = 0; i < Math.min(20, r); i++) {
          const a = rand(i) * 6.28;
          const d = rand(i * 3 + 1) * r;
          const up = ((t * 8 + i * 2) % 8) | 0;
          g.fillStyle = '#b8f818';
          g.fillRect(Math.round(cx + Math.cos(a) * d), Math.round(cy + Math.sin(a) * d) - up, 1, 1);
        }
        ring(g, cx, cy, r, '#58d854', 2);
        break;
      }
      case 'iceGround': {
        disc(g, cx, cy, r, '#a4e4fc', 1);
        disc(g, cx, cy, r * 0.8, '#fcfcfc', 3);
        ring(g, cx, cy, r, '#3cbcfc', 2);
        break;
      }
      case 'sanctuary': {
        disc(g, cx, cy, r, '#f8d878', 2);
        ring(g, cx, cy, r, '#f8d878');
        ring(g, cx, cy, r - 2, '#fcfcfc', 3);
        const n = 6;
        for (let i = 0; i < n; i++) {
          const a = t * 0.8 + (i / n) * 6.28;
          g.fillStyle = '#fcfcfc';
          g.fillRect(Math.round(cx + Math.cos(a) * (r - 4)), Math.round(cy + Math.sin(a) * (r - 4)), 2, 2);
        }
        break;
      }
      case 'singularity': {
        disc(g, cx, cy, r * 0.35, '#000000');
        ring(g, cx, cy, r * 0.35 + 1, '#d800cc');
        for (let i = 0; i < 28; i++) {
          const f = (t * 0.7 + rand(i)) % 1;
          const a = rand(i * 7) * 6.28 + f * 5;
          const d = (1 - f) * r;
          g.fillStyle = i % 2 ? '#b890fc' : '#6844fc';
          g.fillRect(Math.round(cx + Math.cos(a) * d), Math.round(cy + Math.sin(a) * d), 1, 1);
        }
        break;
      }
      case 'curse': {
        disc(g, cx, cy, r, '#6844fc', 2);
        ring(g, cx, cy, r, '#b890fc', 2);
        break;
      }
      case 'mine': {
        const blink = Math.floor(t * 4) % 2 === 0;
        g.fillStyle = '#000000';
        g.fillRect(cx - 4, cy - 2, 8, 5);
        g.fillStyle = '#585858';
        g.fillRect(cx - 3, cy - 2, 6, 4);
        g.fillStyle = blink ? '#f83800' : '#600000';
        g.fillRect(cx - 1, cy - 1, 2, 2);
        break;
      }
      default: {
        disc(g, cx, cy, r, '#fcfcfc', 3);
        ring(g, cx, cy, r, '#bcbcbc', 2);
      }
    }
  }

  private drawTelegraphs(g: CanvasRenderingContext2D, st: GameState): void {
    for (const tg of st.telegraphs) {
      const cx = this.toScreenX(tg.x);
      const cy = this.toScreenY(tg.y);
      const r = tg.r * S;
      const col = ELEMENT_COLORS[tg.el] === '#fcfcfc' ? '#f83800' : ELEMENT_COLORS[tg.el] ?? '#f83800';
      const f = Math.min(1, tg.t / tg.dur);
      disc(g, cx, cy, r * f, col, 2);
      ring(g, cx, cy, r, col);
      if (Math.floor(this.time * 12) % 2 === 0) ring(g, cx, cy, r - 1, col, 2);
    }
  }

  private drawFlatObjects(g: CanvasRenderingContext2D, st: GameState): void {
    const realm = st.realm!;
    for (const o of st.objects.values()) {
      if (o.type !== 'stairsDown' && o.type !== 'stairsUp') continue;
      const spr = objectSprite(o.type, o.state, o.sub, 0, realm);
      const sx = this.toScreenX(o.x) - (spr.width >> 1);
      const sy = this.toScreenY(o.y) - (spr.height >> 1);
      if (this.hoverObject === o) g.drawImage(tinted(spr, '#fcfcfc'), sx, sy);
      g.drawImage(spr, sx, sy);
    }
  }

  private drawItems(g: CanvasRenderingContext2D, st: GameState): void {
    for (const it of st.items.values()) {
      const age = (performance.now() - it.born) / 1000;
      const hop = age < 0.35 ? Math.sin((age / 0.35) * Math.PI) * 10 : 0;
      const sx = this.toScreenX(it.x);
      const sy = this.toScreenY(it.y);
      const spr = itemIcon(it.icon, Math.min(it.rarity, 3));
      if (it.rarity === 2 || it.rarity === 3) {
        // A pillar of light over loot worth stopping for.
        const col = RARITY_COLORS[it.rarity];
        g.fillStyle = col;
        const h = it.rarity === 3 ? 40 : 22;
        for (let i = 0; i < h; i += 2) {
          if (Math.floor(i + this.time * 16) % 4 === 0) g.fillRect(sx, sy - 6 - i, 1, 1);
        }
        for (let i = 0; i < 3; i++) {
          const a = this.time * 2 + i * 2.1;
          g.fillRect(Math.round(sx + Math.cos(a) * 7), Math.round(sy + Math.sin(a) * 3), 1, 1);
        }
      }
      if (this.hoverItem === it) g.drawImage(tinted(spr, '#fcfcfc'), sx - 6, sy - 6 - hop);
      g.drawImage(spr, sx - 6, Math.round(sy - 6 - hop));
    }
  }

  private pickHover(st: GameState, mx: number, my: number): void {
    this.hoverUnit = null;
    this.hoverItem = null;
    this.hoverObject = null;
    let best = Infinity;
    for (const u of st.units.values()) {
      if (u.id === st.myUid) continue;
      if (u.kind !== 'player' && !st.isVisibleAt(u.x, u.y)) continue;
      const dead = u.hp <= 0;
      // Corpses of players can be targeted to revive; monster corpses can't.
      if (dead && u.kind !== 'player') continue;
      const { w, h } = this.unitBox(u);
      const sx = this.toScreenX(u.x);
      const sy = this.toScreenY(u.y);
      if (mx >= sx - w / 2 - 1 && mx <= sx + w / 2 + 1 && my >= sy - h && my <= sy + 3) {
        const d = Math.hypot(mx - sx, my - (sy - h / 2));
        if (d < best) {
          best = d;
          this.hoverUnit = u;
        }
      }
    }
    if (this.hoverUnit) return;
    for (const o of st.objects.values()) {
      const interactive = o.type !== 'chest' && o.type !== 'shrine' ? true : o.state === 0;
      if (!interactive) continue;
      const { w, h, flat } = objBox(o);
      const sx = this.toScreenX(o.x);
      const sy = this.toScreenY(o.y);
      const y0 = flat ? sy - h / 2 : sy - h;
      const y1 = flat ? sy + h / 2 : sy + 3;
      if (mx >= sx - w / 2 && mx <= sx + w / 2 && my >= y0 && my <= y1 && st.isVisibleAt(o.x, o.y)) {
        this.hoverObject = o;
        return;
      }
    }
    let bestD = 12;
    for (const it of st.items.values()) {
      const d = Math.hypot(mx - this.toScreenX(it.x), my - this.toScreenY(it.y));
      if (d < bestD) {
        bestD = d;
        this.hoverItem = it;
      }
    }
  }

  private unitBox(u: CUnit): { w: number; h: number } {
    if (u.kind === 'player') return { w: 14, h: 18 };
    const set = monsterSprite(u.def);
    return { w: set.w, h: set.h };
  }

  private drawSorted(g: CanvasRenderingContext2D, st: GameState, W: number, H: number): void {
    type Row = { y: number; fn: () => void };
    const rows: Row[] = [];
    const camX = this.camX;
    const camY = this.camY;
    const ts = this.tileset!;
    for (const b of this.blockTiles) {
      const px = b.x * S - camX;
      const py = b.y * S - camY;
      if (px < -S * 2 || px > W + S * 2 || py < -S || py > H + S * 3) continue;
      const spr = ts.blocks[b.v];
      if (!spr) continue;
      rows.push({
        y: b.y + 1,
        fn: () => g.drawImage(spr, px + ((S - spr.width) >> 1), py + S - spr.height),
      });
    }
    const realm = st.realm!;
    const frame = Math.floor(this.time * 6);
    for (const o of st.objects.values()) {
      if (o.type === 'stairsDown' || o.type === 'stairsUp') continue;
      rows.push({
        // Portals sit behind everything: you arrive right next to one and must stay visible.
        y: o.type === 'portal' ? o.y - 100 : o.y,
        fn: () => {
          const sx = this.toScreenX(o.x);
          const sy = this.toScreenY(o.y);
          if (o.type === 'npc') {
            const look = NPC_LOOKS[o.sub] ?? NPC_LOOKS.merchant;
            const spr = heroFrame(look, 0, 0);
            this.shadow(g, sx, sy, 6);
            if (this.hoverObject === o) this.outlinedHero(g, spr, sx - 8, sy - 17);
            blitHero(g, spr, sx - 8, sy - 17);
            drawText(g, NPC_NAMES[o.sub] ?? '', sx, sy - 27, '#f8d878', { align: 'center' });
            return;
          }
          const spr = objectSprite(o.type, o.state, o.sub, frame, realm);
          const dx = sx - (spr.width >> 1);
          const dy = sy - spr.height + 4;
          if (this.hoverObject === o) this.outlined(g, spr, dx, dy);
          g.drawImage(spr, dx, dy);
          if (st.zone?.safe) {
            const label = o.type === 'gate' ? 'Waygate' : o.type === 'stash' ? 'Stash' : o.sub === 'arena' ? 'Blood Arena' : o.sub === 'return' ? 'Way Back' : o.sub === 'boss' ? parseZone(o.to).realm.bossName : '';
            if (label) drawText(g, label, sx, dy - 9, '#f8d878', { align: 'center' });
          }
        },
      });
    }
    for (const u of st.units.values()) {
      const dead = u.hp <= 0 && u.lastAnim === ANIM_DEAD;
      if (u.kind !== 'player' && !st.isVisibleAt(u.x, u.y)) continue;
      const sx = u.x * S - camX;
      const sy = u.y * S - camY;
      if (sx < -48 || sx > W + 48 || sy < -48 || sy > H + 60) continue;
      rows.push({ y: dead ? u.y - 100 : u.y, fn: () => this.drawUnit(g, st, u, dead) });
    }
    rows.sort((a, b) => a.y - b.y);
    for (const r of rows) r.fn();
  }

  private shadow(g: CanvasRenderingContext2D, sx: number, sy: number, rx: number): void {
    g.fillStyle = 'rgba(0,0,0,0.35)';
    const ry = Math.max(1, Math.round(rx * 0.4));
    for (let dy = -ry; dy <= ry; dy++) {
      const span = Math.round(rx * Math.sqrt(1 - (dy * dy) / (ry * ry + 0.01)));
      g.fillRect(sx - span, sy + dy, span * 2 + 1, 1);
    }
  }

  private outlined(g: CanvasRenderingContext2D, spr: HTMLCanvasElement, x: number, y: number, color = OUTLINE.unit): void {
    const t = tinted(spr, color);
    g.drawImage(t, x - 1, y);
    g.drawImage(t, x + 1, y);
    g.drawImage(t, x, y - 1);
    g.drawImage(t, x, y + 1);
  }

  /** The hover outline for a hero sprite, one of its own finer pixels wide. */
  private outlinedHero(g: CanvasRenderingContext2D, spr: HTMLCanvasElement, x: number, y: number, color = OUTLINE.unit): void {
    const t = tinted(spr, color);
    blitHero(g, t, x, y, -1, 0);
    blitHero(g, t, x, y, 1, 0);
    blitHero(g, t, x, y, 0, -1);
    blitHero(g, t, x, y, 0, 1);
  }

  private drawUnit(g: CanvasRenderingContext2D, st: GameState, u: CUnit, dead: boolean): void {
    const sx = this.toScreenX(u.x);
    const sy = this.toScreenY(u.y);
    const dir = u.dirAnim & 3;
    const anim = u.dirAnim >> 2;
    const hop = u.hopDur > 0 ? Math.sin((u.hopT / u.hopDur) * Math.PI) * 24 : 0;
    const hovered = this.hoverUnit === u;
    const bits = u.status;
    if (dead) {
      this.drawCorpse(g, u, sx, sy);
      if (u.kind === 'player') {
        drawText(g, `${u.name}`, sx, sy - 12, '#f87858', { align: 'center' });
      }
      return;
    }

    if (u.tier >= 1 && u.kind === 'monster') {
      const col = u.tier === 3 ? '#f83800' : u.tier === 2 ? '#f8d878' : '#6888fc';
      const w = (u.tier === 3 ? 18 : 10);
      ring(g, sx, sy, w, col, 3);
    }
    if (bits & BUFF_BITS.shield) ring(g, sx, sy - 8, 11, '#3cbcfc', 2);
    if (bits & BUFF_BITS.invuln) ring(g, sx, sy - 8, 12, '#f8d878');
    if (bits & BUFF_BITS.rage) {
      for (let i = 0; i < 4; i++) {
        const a = this.time * 4 + i * 1.57;
        g.fillStyle = '#f83800';
        g.fillRect(Math.round(sx + Math.cos(a) * 8), Math.round(sy + Math.sin(a) * 3 - 1), 1, 2);
      }
    }
    if (bits & BUFF_BITS.bless) {
      g.fillStyle = '#f8d878';
      for (let i = 0; i < 3; i++) {
        const a = this.time * 3 + i * 2.09;
        g.fillRect(Math.round(sx + Math.cos(a) * 7), Math.round(sy - 18 + Math.sin(a) * 2), 1, 1);
      }
    }

    let spr: HTMLCanvasElement;
    let ox: number;
    let oy: number;
    let flip = false;

    if (u.kind === 'player') {
      const look = dressedLook(u.def as ClassId, u.gear);
      const walking = anim === ANIM_WALK;
      const f = walking ? 1 + (Math.floor(u.walk) % 2) : 0;
      spr = heroFrame(look, dir, f, Math.floor(this.time * 3) % 2);
      ox = sx - 8;
      oy = sy - 17;
      this.shadow(g, sx, sy, hop > 0 ? 4 : 6);
      if (u.id === st.myUid) {
        // Always find yourself in a crowd.
      } else if (hovered) {
        this.outlinedHero(g, spr, ox, oy - hop);
      }
    } else {
      const set = monsterSprite(u.def);
      const walking = anim === ANIM_WALK;
      const attacking = anim === ANIM_SWING || anim === ANIM_CAST || anim === ANIM_SHOOT || anim === ANIM_SLAM;
      const key = attacking ? 'atk' : walking ? (Math.floor(u.walk) % 2 === 0 ? 'walk1' : 'walk2') : 'idle';
      spr = set.frames[key as 'idle'];
      flip = dir === 2;
      ox = sx - (set.w >> 1);
      oy = sy - set.h + 2;
      this.shadow(g, sx, sy, Math.max(4, Math.round(set.w * 0.38)));
      if (hovered) this.outlined(g, spr, ox, oy, st.units.get(st.myUid)?.team === u.team ? '#58f898' : '#f83800');
    }

    // Status tints.
    let draw = spr;
    if (bits & STATUS_BITS.freeze) draw = blended(spr, '#3cbcfc', 0.65);
    else if (bits & STATUS_BITS.chill) draw = blended(spr, '#3cbcfc', 0.35);
    else if (bits & STATUS_BITS.poison) draw = blended(spr, '#58d854', 0.35);
    else if (bits & STATUS_BITS.burn) draw = blended(spr, '#f87800', 0.3);
    else if (bits & STATUS_BITS.weaken) draw = blended(spr, '#6844fc', 0.3);
    if (u.flash > 0.5) draw = tinted(spr, '#fcfcfc');
    else if (u.flash > 0.1) draw = blended(spr, '#f83800', 0.5);

    const lunge = this.lunge(u, anim, dir);
    const dx = ox + lunge[0];
    const dy = Math.round(oy + lunge[1] - hop);

    const drawWeaponFirst = u.kind === 'player' && heldBehind(u.gear.weapon, dir, anim, u.animT, this.time);
    if (drawWeaponFirst) this.drawHeld(g, u, sx + lunge[0], sy + lunge[1] - hop, dir, anim);
    if (u.kind === 'player') {
      blitHero(g, draw, dx, dy);
    } else if (flip) {
      g.save();
      g.translate(dx + spr.width, dy);
      g.scale(-1, 1);
      g.drawImage(draw, 0, 0);
      g.restore();
    } else {
      g.drawImage(draw, dx, dy);
    }
    if (u.kind === 'player' && !drawWeaponFirst) this.drawHeld(g, u, sx + lunge[0], sy + lunge[1] - hop, dir, anim);

    if (bits & STATUS_BITS.stun) {
      const h = u.kind === 'player' ? 20 : this.unitBox(u).h;
      for (let i = 0; i < 3; i++) {
        const a = this.time * 7 + i * 2.09;
        g.fillStyle = '#f8d878';
        g.fillRect(Math.round(sx + Math.cos(a) * 6), Math.round(sy - h - 2 + Math.sin(a) * 2), 1, 1);
      }
    }
    if (u.kind === 'player' && u.id !== st.myUid) {
      const foe = u.team !== st.units.get(st.myUid)?.team;
      drawText(g, u.name, sx, sy - 27 - hop, foe ? '#f87858' : '#58f898', { align: 'center' });
    }
  }

  private lunge(u: CUnit, anim: number, dir: number): [number, number] {
    if (anim !== ANIM_SWING && anim !== ANIM_SLAM) return [0, 0];
    const f = Math.min(1, u.animT / 0.18);
    const amt = Math.sin(f * Math.PI) * 3;
    if (dir === 0) return [0, Math.round(amt)];
    if (dir === 1) return [0, -Math.round(amt)];
    if (dir === 2) return [-Math.round(amt), 0];
    return [Math.round(amt), 0];
  }

  /** The hero's weapon, swung through a few discrete angles to stay crunchy. */
  private drawHeld(g: CanvasRenderingContext2D, u: CUnit, sx: number, sy: number, dir: number, anim: number): void {
    drawHeldWeapon(g, u.gear.weapon, u.gear.weaponTier, u.gear.weaponRarity, sx, sy, dir, anim, u.animT, this.time);
  }

  private drawCorpse(g: CanvasRenderingContext2D, u: CUnit, sx: number, sy: number): void {
    if (u.kind === 'player') {
      const look = dressedLook(u.def as ClassId, u.gear);
      const spr = blended(heroFrame(look, 0, 0), '#600000', 0.45);
      g.save();
      g.translate(sx, sy - 2);
      g.rotate(Math.PI / 2);
      blitHero(g, spr, -14, -8);
      g.restore();
      return;
    }
    const def = MONSTERS[u.def];
    if (def?.noCorpse) return;
    const set = monsterSprite(u.def);
    const spr = blended(set.frames.idle, '#300000', 0.55);
    const hFactor = 0.45;
    const h = Math.max(4, Math.round(set.h * hFactor));
    g.drawImage(spr, sx - (set.w >> 1), sy - h + 2, set.w, h);
    g.fillStyle = '#600000';
    for (let i = 0; i < 6; i++) {
      g.fillRect(sx - 6 + Math.floor(rand(u.id + i) * 12), sy - 1 + Math.floor(rand(u.id * 3 + i) * 4), 1, 1);
    }
  }

  private drawProjectiles(g: CanvasRenderingContext2D, st: GameState): void {
    for (const p of st.projs.values()) {
      if (p.life < 0) continue;
      this.drawProjectile(g, p);
    }
  }

  private drawProjectile(g: CanvasRenderingContext2D, p: CProj): void {
    let sx = p.x * S - this.camX;
    let sy = p.y * S - this.camY;
    let lift = 8;
    if (p.lob) {
      const f = 1 - Math.max(0, p.life) / Math.max(0.01, p.total);
      lift = 4 + Math.sin(f * Math.PI) * 26;
    }
    const groundY = Math.round(sy);
    sy = Math.round(sy - lift);
    sx = Math.round(sx);
    const speed = Math.hypot(p.vx, p.vy) || 1;
    const dx = p.vx / speed;
    const dy = p.vy / speed;
    const col = projColor(p.vis);
    g.fillStyle = 'rgba(0,0,0,0.3)';
    g.fillRect(sx - 1, groundY - 1, 3, 2);
    const trail = (cols: string[], step: number, size: number) => {
      for (let i = cols.length - 1; i >= 0; i--) {
        g.fillStyle = cols[i];
        const s = Math.max(1, size - i);
        g.fillRect(Math.round(sx - dx * step * i) - (s >> 1), Math.round(sy - dy * step * i) - (s >> 1), s, s);
      }
    };
    switch (p.vis) {
      case 'firebolt':
        trail(['#fcfcfc', '#f8d878', '#f87800', '#f83800', '#a81000'], 2, 4);
        break;
      case 'fireball':
        trail(['#fcfcfc', '#f8d878', '#f87800', '#f83800', '#a81000', '#600000'], 3, 7);
        break;
      case 'icebolt':
        trail(['#fcfcfc', '#a4e4fc', '#3cbcfc', '#0078f8'], 2, 4);
        break;
      case 'spark': {
        const f = Math.floor(this.time * 30);
        g.fillStyle = f % 2 ? '#fcfcfc' : '#f8d878';
        g.fillRect(sx - 1, sy - 1, 3, 3);
        g.fillStyle = '#f8d878';
        for (let i = 0; i < 3; i++) g.fillRect(sx + Math.floor(rand(f + i) * 9) - 4, sy + Math.floor(rand(f * 2 + i) * 9) - 4, 1, 1);
        break;
      }
      case 'bone':
        g.fillStyle = '#fcfcfc';
        g.fillRect(sx - 2, sy, 5, 1);
        g.fillRect(sx - 2, sy - 1, 1, 3);
        g.fillRect(sx + 2, sy - 1, 1, 3);
        break;
      case 'poison':
      case 'spit':
        trail(['#b8f818', '#58d854', '#006800'], 2, 4);
        break;
      case 'arcane':
        trail(['#fcfcfc', '#b890fc', '#6844fc', '#2038ec'], 2, 4);
        break;
      case 'void':
        trail(['#000000', '#d800cc', '#6844fc'], 3, 7);
        break;
      case 'plasma':
        trail(['#fcfcfc', '#58f8f8', '#00e8d8', '#008888'], 2, 5);
        break;
      case 'bullet':
        trail(['#fcfcfc', '#f8d878', '#f87800'], 2, 2);
        break;
      case 'pellet':
        g.fillStyle = '#f8d878';
        g.fillRect(sx, sy, 2, 2);
        break;
      case 'laser':
        g.fillStyle = '#f83800';
        for (let i = 0; i < 9; i++) g.fillRect(Math.round(sx - dx * i), Math.round(sy - dy * i), 1, 1);
        g.fillStyle = '#fcfcfc';
        for (let i = 0; i < 4; i++) g.fillRect(Math.round(sx - dx * i), Math.round(sy - dy * i), 1, 1);
        break;
      case 'rocket':
        g.fillStyle = '#bcbcbc';
        g.fillRect(sx - 2, sy - 2, 4, 4);
        g.fillStyle = '#f83800';
        g.fillRect(Math.round(sx + dx * 2), Math.round(sy + dy * 2), 2, 2);
        trail(['#f8d878', '#f87800', '#7c7c7c', '#585858'], 3, 3);
        break;
      case 'grenade': {
        g.fillStyle = '#006800';
        g.fillRect(sx - 2, sy - 2, 4, 4);
        g.fillStyle = '#000000';
        g.fillRect(sx - 1, sy - 3, 2, 1);
        break;
      }
      case 'holy':
        trail(['#fcfcfc', '#f8d878', '#f8b800'], 2, 4);
        g.fillStyle = '#fcfcfc';
        g.fillRect(sx - 3, sy, 7, 1);
        g.fillRect(sx, sy - 3, 1, 7);
        break;
      case 'hammer': {
        const a = this.time * 14;
        g.save();
        g.translate(sx, sy);
        g.rotate(Math.round(a / (Math.PI / 4)) * (Math.PI / 4));
        g.fillStyle = '#f8d878';
        g.fillRect(-4, -3, 8, 4);
        g.fillStyle = '#887000';
        g.fillRect(-1, 1, 2, 5);
        g.restore();
        break;
      }
      case 'arrow':
        g.fillStyle = '#d8b878';
        for (let i = 0; i < 6; i++) g.fillRect(Math.round(sx - dx * i), Math.round(sy - dy * i), 1, 1);
        g.fillStyle = '#fcfcfc';
        g.fillRect(sx, sy, 1, 1);
        break;
      case 'wave': {
        const px = -dy;
        const py = dx;
        for (let i = -3; i <= 3; i++) {
          g.fillStyle = Math.abs(i) > 1 ? '#3cbcfc' : '#fcfcfc';
          g.fillRect(Math.round(sx + px * i * 2 - dx * Math.abs(i)), Math.round(sy + py * i * 2 - dy * Math.abs(i)), 2, 2);
        }
        break;
      }
      case 'shard':
        trail(['#fcfcfc', '#d8b8f8', '#b890fc'], 2, 3);
        break;
      default:
        g.fillStyle = col;
        g.fillRect(sx - 1, sy - 1, 3, 3);
    }
  }

  private drawEffects(g: CanvasRenderingContext2D, st: GameState): void {
    for (const e of st.effects) this.drawEffect(g, e);
  }

  private drawEffect(g: CanvasRenderingContext2D, e: CEffect): void {
    const cx = this.toScreenX(e.x);
    const cy = this.toScreenY(e.y);
    const f = Math.min(1, e.t / e.dur);
    const r = e.r * S;
    switch (e.vis) {
      case 'slash':
      case 'slashRed':
      case 'slashBig':
      case 'slashWhite':
      case 'clawHit':
      case 'coldHit':
      case 'poisonHit': {
        const cols: Record<string, [string, string]> = {
          slash: ['#fcfcfc', '#bcbcbc'], slashRed: ['#fcfcfc', '#f83800'], slashBig: ['#fcfcfc', '#f8d878'], slashWhite: ['#fcfcfc', '#a4e4fc'],
          clawHit: ['#fcfcfc', '#d82800'], coldHit: ['#fcfcfc', '#3cbcfc'], poisonHit: ['#b8f818', '#58d854'],
        };
        const [c1, c2] = cols[e.vis];
        const spread = e.vis === 'slashBig' ? 1.5 : 1.1;
        const head = e.ang - spread + f * spread * 2.4;
        const steps = 22;
        for (let i = 0; i < steps; i++) {
          const a = head - (i / steps) * spread;
          if (a < e.ang - spread || a > e.ang + spread) continue;
          const rad = Math.max(10, r * 0.9);
          for (let t = 0; t < 3; t++) {
            g.fillStyle = i < 5 ? c1 : c2;
            if (i > 14 && (i + t) % 2) continue;
            g.fillRect(Math.round(cx + Math.cos(a) * (rad - t * 2)), Math.round(cy - 6 + Math.sin(a) * (rad - t * 2)), 1, 1);
          }
        }
        break;
      }
      case 'whirl': {
        for (let i = 0; i < 3; i++) {
          const a = f * 6.28 * 2 + i * 2.09;
          for (let j = 0; j < 12; j++) {
            const b = a - j * 0.13;
            g.fillStyle = j < 4 ? '#fcfcfc' : '#bcbcbc';
            g.fillRect(Math.round(cx + Math.cos(b) * r), Math.round(cy - 4 + Math.sin(b) * r), 1, 1);
          }
        }
        break;
      }
      case 'shockwave':
      case 'shockwaveRed':
        ring(g, cx, cy, r * f, e.vis === 'shockwave' ? '#d8b878' : '#f83800');
        ring(g, cx, cy, r * f - 1, '#fcfcfc', 2);
        ring(g, cx, cy, r * f * 0.7, '#8c7c60', 2);
        break;
      case 'frostnova':
        ring(g, cx, cy, r * f, '#a4e4fc');
        ring(g, cx, cy, r * f - 1, '#fcfcfc', 2);
        disc(g, cx, cy, r * f, '#3cbcfc', 2);
        for (let i = 0; i < 10; i++) {
          const a = (i / 10) * 6.28;
          g.fillStyle = '#fcfcfc';
          g.fillRect(Math.round(cx + Math.cos(a) * r * f), Math.round(cy + Math.sin(a) * r * f) - 2, 1, 4);
        }
        break;
      case 'holynova':
        ring(g, cx, cy, r * f, '#f8d878');
        ring(g, cx, cy, r * f - 1, '#fcfcfc');
        disc(g, cx, cy, r * f, '#f8d878', 3);
        break;
      case 'explosion':
      case 'explosionGreen':
      case 'meteor': {
        const green = e.vis === 'explosionGreen';
        const rr = r * Math.min(1, f * 2.2);
        const a = 1 - f;
        const cOut = green ? '#006800' : '#a81000';
        const cMid = green ? '#58d854' : '#f87800';
        const cIn = green ? '#b8f818' : '#f8d878';
        disc(g, cx, cy, rr, cOut, f > 0.55 ? 2 : f > 0.3 ? 1 : 0);
        disc(g, cx, cy, rr * 0.7 * a + 2, cMid, f > 0.45 ? 1 : 0);
        disc(g, cx, cy, rr * 0.4 * a, cIn);
        if (f < 0.2) disc(g, cx, cy, rr * 0.25, '#fcfcfc');
        ring(g, cx, cy, r * f * 1.1, '#585858', 2);
        if (e.vis === 'meteor' && f < 0.1) {
          g.fillStyle = '#f8d878';
          g.fillRect(cx - 1, cy - 200, 3, 200);
        }
        break;
      }
      case 'orbital': {
        const w = Math.max(2, Math.round(r * 0.5 * (1 - f)));
        g.fillStyle = '#a4e4fc';
        g.fillRect(cx - w, cy - 300, w * 2, 300);
        g.fillStyle = '#fcfcfc';
        g.fillRect(cx - (w >> 1), cy - 300, w, 300);
        disc(g, cx, cy, r * Math.min(1, f * 3), '#a4e4fc', f > 0.4 ? 2 : 1);
        ring(g, cx, cy, r * f * 1.2, '#fcfcfc');
        break;
      }
      case 'sparkBurst':
        for (let i = 0; i < 12; i++) {
          const a = (i / 12) * 6.28 + rand(i) * 0.5;
          const d0 = r * f * 0.6;
          const d1 = r * f;
          g.fillStyle = i % 2 ? '#fcfcfc' : '#f8d878';
          for (let j = 0; j < 4; j++) {
            const d = d0 + ((d1 - d0) * j) / 4;
            g.fillRect(Math.round(cx + Math.cos(a) * d), Math.round(cy + Math.sin(a) * d), 1, 1);
          }
        }
        break;
      case 'voidBurst':
        disc(g, cx, cy, r * (1 - f) * 0.6, '#000000');
        ring(g, cx, cy, r * f, '#d800cc');
        ring(g, cx, cy, r * f * 0.7, '#6844fc', 2);
        break;
      case 'lightning':
        g.fillStyle = '#fcfcfc';
        g.fillRect(cx, cy - 200, 2, 200);
        break;
      case 'levelup': {
        const h = Math.round(90 * Math.min(1, f * 3));
        g.fillStyle = '#f8d878';
        g.fillRect(cx - 6, cy - h, 1, h);
        g.fillRect(cx + 6, cy - h, 1, h);
        g.fillStyle = '#fcfcfc';
        g.fillRect(cx, cy - h, 1, h);
        ring(g, cx, cy, 10 + f * 12, '#f8d878');
        break;
      }
      default:
        // Puffs & buffs are particles; nothing extra to draw.
        break;
    }
  }

  private drawLines(g: CanvasRenderingContext2D, st: GameState): void {
    for (const l of st.lines) {
      const pts = l.pts;
      if (pts.length < 4) continue;
      const jag = l.vis === 'lightning';
      const seedT = Math.floor(l.t * 40);
      for (let i = 0; i + 3 < pts.length; i += 2) {
        const x0 = pts[i] * S - this.camX;
        const y0 = pts[i + 1] * S - this.camY - 8;
        const x1 = pts[i + 2] * S - this.camX;
        const y1 = pts[i + 3] * S - this.camY - 8;
        const dist = Math.hypot(x1 - x0, y1 - y0);
        const n = Math.max(2, Math.floor(dist / (jag ? 6 : 1)));
        let lx = x0;
        let ly = y0;
        for (let j = 1; j <= n; j++) {
          const f = j / n;
          let nx = x0 + (x1 - x0) * f;
          let ny = y0 + (y1 - y0) * f;
          if (jag && j < n) {
            nx += (rand(seedT + j * 7 + i) - 0.5) * 9;
            ny += (rand(seedT * 3 + j + i) - 0.5) * 9;
          }
          this.pixelLine(g, lx, ly, nx, ny, jag ? '#fcfcfc' : l.vis === 'siphon' ? (j % 2 ? '#f83800' : '#6844fc') : '#b890fc');
          if (jag) this.pixelLine(g, lx + 1, ly, nx + 1, ny, '#f8d878');
          lx = nx;
          ly = ny;
        }
      }
    }
  }

  private pixelLine(g: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number, col: string): void {
    g.fillStyle = col;
    const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0)) | 0;
    if (n === 0) {
      g.fillRect(Math.round(x0), Math.round(y0), 1, 1);
      return;
    }
    for (let i = 0; i <= n; i++) {
      g.fillRect(Math.round(x0 + ((x1 - x0) * i) / n), Math.round(y0 + ((y1 - y0) * i) / n), 1, 1);
    }
  }

  private drawParticles(g: CanvasRenderingContext2D, st: GameState): void {
    for (const p of st.particles) {
      g.fillStyle = p.color;
      g.fillRect(Math.round(p.x - this.camX), Math.round(p.y - this.camY), p.size, p.size);
    }
  }

  // ─── Lighting ───────────────────────────────────────────────────────────

  private drawLighting(g: CanvasRenderingContext2D, st: GameState, W: number, H: number, dt: number): void {
    const map = st.map!;
    const realm = st.realm!;
    if (this.lightCanvas.width !== W || this.lightCanvas.height !== H || !this.lightImg) {
      this.lightCanvas.width = W;
      this.lightCanvas.height = H;
      this.lightImg = this.lightCtx.createImageData(W, H);
    }
    const lights: Light[] = [];
    const me = st.units.get(st.myUid);
    const flicker = 1 + Math.sin(this.time * 9) * 0.015 + Math.sin(this.time * 23) * 0.012;
    if (me) lights.push({ x: me.x, y: me.y - 0.3, r: realm.torch * flicker, a: 1 });
    else lights.push({ x: st.px, y: st.py, r: realm.torch, a: 1 });
    for (const u of st.units.values()) {
      if (u.id === st.myUid || u.kind !== 'player' || u.hp <= 0) continue;
      lights.push({ x: u.x, y: u.y, r: realm.torch * 0.65, a: 0.8 });
    }
    for (const p of st.projs.values()) {
      if (p.vis === 'bullet' || p.vis === 'pellet' || p.vis === 'bone' || p.vis === 'arrow') continue;
      lights.push({ x: p.x, y: p.y, r: 3.2, a: 0.8 });
    }
    for (const gr of st.grounds.values()) {
      if (gr.vis !== 'mine') lights.push({ x: gr.x, y: gr.y, r: gr.r + 2.5, a: 0.6 });
    }
    for (const e of st.effects) {
      if (e.vis.startsWith('explosion') || e.vis === 'meteor' || e.vis === 'orbital' || e.vis === 'frostnova' || e.vis === 'holynova' || e.vis === 'levelup') {
        lights.push({ x: e.x, y: e.y, r: e.r + 4, a: 1 - e.t / e.dur });
      }
    }
    for (const o of st.objects.values()) {
      if (o.type === 'portal' || o.type === 'gate' || (o.type === 'shrine' && o.state === 0)) {
        lights.push({ x: o.x, y: o.y, r: o.type === 'gate' ? 8 : 5, a: 0.75 });
      }
    }

    // Smooth per-tile visibility so that shadows sweep rather than pop.
    const w = map.w;
    const h = map.h;
    const tx0 = Math.max(0, Math.floor(this.camX / S) - 2);
    const ty0 = Math.max(0, Math.floor(this.camY / S) - 2);
    const tx1 = Math.min(w - 1, Math.ceil((this.camX + W) / S) + 2);
    const ty1 = Math.min(h - 1, Math.ceil((this.camY + H) / S) + 2);
    const vs = this.visSmooth;
    const rate = Math.min(1, dt * 12);
    for (let y = ty0; y <= ty1; y++) {
      for (let x = tx0; x <= tx1; x++) {
        const i = y * w + x;
        const target = st.visible[i] ? 1 : st.explored[i] ? 0.28 : 0;
        vs[i] += (target - vs[i]) * rate;
      }
    }

    const CELL = 4;
    const cw = Math.ceil(W / CELL) + 1;
    const ch = Math.ceil(H / CELL) + 1;
    const cells = new Float32Array(cw * ch);
    const ambient = realm.ambient;
    for (let cy = 0; cy < ch; cy++) {
      const wy = (cy * CELL + this.camY) / S;
      for (let cx = 0; cx < cw; cx++) {
        const wx = (cx * CELL + this.camX) / S;
        // Bilinear sample of tile visibility, using tile centers.
        const fx = wx - 0.5;
        const fy = wy - 0.5;
        const ix = Math.floor(fx);
        const iy = Math.floor(fy);
        const ax = fx - ix;
        const ay = fy - iy;
        let vis = 0;
        if (ix >= 0 && iy >= 0 && ix + 1 < w && iy + 1 < h) {
          const i = iy * w + ix;
          vis = (vs[i] * (1 - ax) + vs[i + 1] * ax) * (1 - ay) + (vs[i + w] * (1 - ax) + vs[i + w + 1] * ax) * ay;
        }
        let light = ambient;
        for (const L of lights) {
          const dx = wx - L.x;
          const dy = wy - L.y;
          const d2 = dx * dx + dy * dy;
          const rr = L.r * L.r;
          if (d2 >= rr) continue;
          const v = (1 - d2 / rr) * L.a;
          const v2 = v * (2 - v);
          if (v2 > light) light = v2;
        }
        cells[cy * cw + cx] = Math.min(1, light) * vis;
      }
    }

    const img = this.lightImg;
    const data = new Uint32Array(img.data.buffer);
    // Darkness levels from the Bayer threshold. The steps keep the palette chunky.
    const LEVELS = [0xff000000, 0xc8000000, 0x90000000, 0x58000000, 0x28000000, 0x00000000];
    const steps = LEVELS.length - 1;
    const ox = ((this.camX % 4) + 4) % 4;
    const oy = ((this.camY % 4) + 4) % 4;
    for (let y = 0; y < H; y++) {
      const cyi = y >> 2;
      const fy = (y & 3) / 4;
      const row = y * W;
      const by = (y + oy) & 3;
      for (let x = 0; x < W; x++) {
        const cxi = x >> 2;
        const fx = (x & 3) / 4;
        const c0 = cyi * cw + cxi;
        const v =
          (cells[c0] * (1 - fx) + cells[c0 + 1] * fx) * (1 - fy) + (cells[c0 + cw] * (1 - fx) + cells[c0 + cw + 1] * fx) * fy;
        const t = v * steps + BAYER4[by * 4 + ((x + ox) & 3)] - 0.5;
        const lvl = t <= 0 ? 0 : t >= steps ? steps : Math.round(t);
        data[row + x] = LEVELS[lvl];
      }
    }
    this.lightCtx.putImageData(img, 0, 0);
    g.drawImage(this.lightCanvas, 0, 0);
  }

  // ─── Overlays above the lighting ────────────────────────────────────────

  private drawUnitBars(g: CanvasRenderingContext2D, st: GameState): void {
    for (const u of st.units.values()) {
      if (u.id === st.myUid || u.hp <= 0) continue;
      if (u.kind !== 'player' && !st.isVisibleAt(u.x, u.y)) continue;
      if (u.tier === 3) continue;
      if (u.hp >= 100 && u.kind === 'monster' && this.hoverUnit !== u) continue;
      const sx = this.toScreenX(u.x);
      const sy = this.toScreenY(u.y);
      const { h } = this.unitBox(u);
      const bw = u.kind === 'player' ? 16 : Math.max(12, Math.min(26, this.unitBox(u).w));
      const by = sy - h - (u.kind === 'player' ? 8 : 3);
      g.fillStyle = '#000000';
      g.fillRect(sx - (bw >> 1) - 1, by - 1, bw + 2, 4);
      g.fillStyle = '#400000';
      g.fillRect(sx - (bw >> 1), by, bw, 2);
      g.fillStyle = u.kind === 'monster' ? '#f83800' : '#58d854';
      g.fillRect(sx - (bw >> 1), by, Math.max(1, Math.round((bw * u.hp) / 100)), 2);
    }
  }

  private drawItemLabels(g: CanvasRenderingContext2D, st: GameState, showAll: boolean, mx: number, my: number): void {
    const placed: { x: number; y: number; w: number }[] = [];
    for (const it of st.items.values()) {
      const hover = this.hoverItem === it;
      if (!showAll && !hover && it.rarity !== 2 && it.rarity !== 3) continue;
      const col = it.rarity === 4 ? '#f8d878' : it.rarity === 5 ? '#58f898' : RARITY_COLORS[it.rarity] ?? '#fcfcfc';
      const sx = this.toScreenX(it.x);
      let sy = this.toScreenY(it.y) - 15;
      const w = textWidth(it.name) + 4;
      for (const p of placed) {
        if (Math.abs(p.x - sx) < (p.w + w) / 2 && Math.abs(p.y - sy) < 9) sy = p.y - 10;
      }
      placed.push({ x: sx, y: sy, w });
      g.fillStyle = hover ? 'rgba(60,40,0,0.9)' : 'rgba(0,0,0,0.6)';
      g.fillRect(sx - (w >> 1), sy - 1, w, 9);
      drawText(g, it.name, sx, sy, col, { align: 'center', shadow: null });
    }
    void mx;
    void my;
  }

  private drawFloats(g: CanvasRenderingContext2D, st: GameState): void {
    for (const f of st.floats) {
      const k = f.t / f.dur;
      const sx = Math.round(f.x * S - this.camX + f.vx * f.t);
      const sy = Math.round(f.y * S - this.camY - k * 18 - 8);
      if (f.big && k < 0.35) {
        drawText(g, f.text, sx, sy - 4, f.color, { align: 'center', scale: 2 });
      } else {
        drawText(g, f.text, sx, sy, f.color, { align: 'center' });
      }
    }
  }
}

function objBox(o: CObject): { w: number; h: number; flat: boolean } {
  switch (o.type) {
    case 'stairsDown':
    case 'stairsUp':
      return { w: 24, h: 24, flat: true };
    case 'portal':
      return { w: 22, h: 30, flat: false };
    case 'gate':
      return { w: 44, h: 46, flat: false };
    case 'npc':
      return { w: 14, h: 22, flat: false };
    case 'shrine':
      return { w: 16, h: 24, flat: false };
    default:
      return { w: 18, h: 16, flat: false };
  }
}

export function objLabel(o: CObject, zoneName: (to: string) => string): string {
  switch (o.type) {
    case 'stairsDown':
      return `Descend to ${zoneName(o.to)}`;
    case 'stairsUp':
      return `Ascend to ${zoneName(o.to)}`;
    case 'portal':
      return o.sub === 'arena' ? 'The Blood Arena' : o.sub === 'hub' ? 'Warp to the Nexus' : `Portal to ${zoneName(o.to)}`;
    case 'gate':
      return 'Waygate';
    case 'npc':
      return NPC_NAMES[o.sub] ?? 'Stranger';
    case 'stash':
      return 'Stash';
    case 'chest':
      return 'Chest';
    case 'shrine':
      return `${o.sub[0]?.toUpperCase() ?? ''}${o.sub.slice(1)} Shrine`;
    default:
      return '';
  }
}

/** The hero's weapon, swung through a few discrete angles to stay crunchy. Shared with the gear lab. */
/**
 * How each kind of weapon is carried and used, for a hero facing right (mirrored for left).
 * Directions are eighths of a turn clockwise from right: 0 right, 2 down, 4 left, 6 up. Arcs
 * have one entry per 40ms of the attack.
 */
interface HeldStyle {
  /** Direction at rest, and how far forward of the hand it is carried in side view. */
  rest: number;
  restDx: number;
  swing: number[];
  slam: number[];
  /** Direction while casting, and how far the grip lifts and thrusts forward. */
  cast: number;
  castLift: number;
  castThrust: number;
  /** How far the grip rises during the wind-up of a swing. */
  windLift: number;
}

const CHOP = [6, 7, 0, 1, 2, 2, 1, 6];
const HEAVY = [6, 6, 7, 0, 1, 2, 2, 2];
const SLASH = [5, 6, 7, 0, 1, 1, 0, 7];
const SWEEP = [5, 6, 7, 0, 1, 2, 2, 1];
const WHACK = [6, 7, 0, 1, 0, 7, 6, 6];
const FLICK = [7, 0, 1, 1, 0, 7, 1, 1];
const LEVEL = [0, 0, 0, 0, 0, 0, 0, 0];

const HELD_STYLES: Record<string, HeldStyle> = {
  sword: { rest: 7, restDx: 0, swing: SLASH, slam: SWEEP, cast: 6, castLift: 2, castThrust: 0, windLift: 2 },
  axe: { rest: 6, restDx: 3, swing: CHOP, slam: SWEEP, cast: 6, castLift: 2, castThrust: 0, windLift: 3 },
  mace: { rest: 6, restDx: 3, swing: CHOP, slam: SWEEP, cast: 6, castLift: 3, castThrust: 0, windLift: 3 },
  hammer: { rest: 6, restDx: 3, swing: HEAVY, slam: HEAVY, cast: 6, castLift: 3, castThrust: 0, windLift: 4 },
  staff: { rest: 6, restDx: 3, swing: WHACK, slam: CHOP, cast: 7, castLift: 1, castThrust: 2, windLift: 1 },
  wand: { rest: 7, restDx: 1, swing: FLICK, slam: FLICK, cast: 0, castLift: 0, castThrust: 3, windLift: 1 },
  scepter: { rest: 6, restDx: 2, swing: WHACK, slam: CHOP, cast: 6, castLift: 4, castThrust: 0, windLift: 2 },
  scythe: { rest: 6, restDx: 3, swing: SWEEP, slam: SWEEP, cast: 6, castLift: 2, castThrust: 0, windLift: 3 },
  pistol: { rest: 0, restDx: 1, swing: LEVEL, slam: LEVEL, cast: 0, castLift: 0, castThrust: 1, windLift: 0 },
  rifle: { rest: 0, restDx: 0, swing: LEVEL, slam: LEVEL, cast: 0, castLift: 0, castThrust: 1, windLift: 0 },
};
const DEFAULT_STYLE: HeldStyle = HELD_STYLES.sword;
const ARC_STEP = 0.04;

interface HeldPose {
  /** Direction for a hero facing right, before mirroring. */
  a: number;
  flip: boolean;
  /** Grip offset in logical pixels, forward and down. */
  ox: number;
  oy: number;
  flash: boolean;
}

function heldPoseFor(icon: string, dir: number, anim: number, animT: number, time: number): HeldPose {
  const st = HELD_STYLES[icon] ?? DEFAULT_STYLE;
  const side = dir === 1 || dir === 2 ? -1 : 1;
  const flat = heldPose(icon) === 'flat';
  let a = st.rest;
  let flip = side < 0;
  let ox = dir >= 2 ? st.restDx : 0;
  let oy = 0;
  let flash = false;
  if (anim === ANIM_WHIRL) {
    a = Math.floor(time * 24) % 8;
    flip = false;
  } else if (anim === ANIM_SWING || anim === ANIM_SLAM) {
    const arc = anim === ANIM_SLAM ? st.slam : st.swing;
    const step = Math.min(arc.length - 1, Math.floor(animT / ARC_STEP));
    a = arc[step];
    if (step <= 1) {
      ox = -1;
      oy = -st.windLift;
    } else if (step >= 3 && step <= 5) {
      ox = 2;
      oy = 1;
    }
  } else if (anim === ANIM_SHOOT) {
    // Guns fire level, except when facing away, where they aim up the screen. Facing the camera,
    // a gun pointed straight down would only read as a stub, so it stays level and the shot shows
    // the aim.
    a = flat ? (dir === 1 ? 6 : 0) : st.cast;
    if (animT < 0.06) ox = -2;
    flash = animT < 0.1;
  } else if (anim === ANIM_CAST) {
    a = st.cast;
    oy = -st.castLift;
    ox = st.castThrust;
  }
  if (flat && (a & 1)) a = (a + 1) % 8;
  return { a, flip, ox, oy, flash };
}

/** Whether the weapon goes behind the hero: raised back over the shoulder, or carried while facing away. */
export function heldBehind(icon: string, dir: number, anim: number, animT: number, time: number): boolean {
  if (!icon) return false;
  const { a } = heldPoseFor(icon, dir, anim, animT, time);
  const back = a >= 3 && a <= 5;
  return (dir === 1) !== back;
}

export function drawHeldWeapon(g: CanvasRenderingContext2D, icon: string, tier: number, rarity: number, sx: number, sy: number, dir: number, anim: number, animT: number, time: number): void {
  if (!icon) return;
  const pose = heldPoseFor(icon, dir, anim, animT, time);
  // The weapon hand: on the hero's right, which is the viewer's left when facing away or left.
  const side = dir === 1 || dir === 2 ? -1 : 1;
  const a = pose.flip ? (4 - pose.a + 8) % 8 : pose.a;
  const spr = heldWeapon(icon, tier, rarity, a, pose.flip);
  if (!spr) return;
  const hx = Math.round(sx + side * (dir >= 2 ? 1 : 4) + pose.ox * (pose.flip ? -1 : 1));
  const hy = Math.round(sy - 5 + pose.oy);
  blitHeld(g, spr, hx, hy);
  if (pose.flash || anim === ANIM_CAST) {
    const ang = (a * Math.PI) / 4;
    const reach = heldReach(icon);
    const tx = hx + Math.cos(ang) * reach;
    const ty = hy + Math.sin(ang) * reach;
    if (pose.flash) {
      // Muzzle flash: a bright cross at the tip.
      g.fillStyle = '#fcfcfc';
      g.fillRect(Math.round(tx) - 1, Math.round(ty) - 1, 3, 3);
      g.fillStyle = '#f8d878';
      g.fillRect(Math.round(tx) - 3, Math.round(ty), 7, 1);
      g.fillRect(Math.round(tx), Math.round(ty) - 3, 1, 7);
    } else {
      const t = time * 12;
      g.fillStyle = '#fcfcfc';
      g.fillRect(Math.round(tx + Math.cos(t) * 3), Math.round(ty + Math.sin(t) * 3), 1, 1);
      g.fillStyle = '#b890fc';
      g.fillRect(Math.round(tx + Math.cos(t + 3) * 3), Math.round(ty + Math.sin(t + 3) * 3), 1, 1);
      g.fillRect(Math.round(tx + Math.cos(t + 1.5) * 2), Math.round(ty - 1 + Math.sin(t + 1.5) * 2), 1, 1);
    }
  }
}
